import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useDeals } from "@/api/crm";
import { useContact, useDeleteContact, useFields, useMe } from "@/api/hooks";
import { StatusBadge } from "@/components/crm/Badge";
import { formatCustom, useFieldLabels } from "@/components/crm/CustomFieldsFieldset";
import { ActivityComposer, Timeline } from "@/components/crm/Timeline";
import { Alert, Avatar, PageHeader, Spinner, formatDate } from "@/components/ui";
import { money } from "@/lib/format";
import { can } from "@/lib/permissions";
import { CallButtons } from "@/features/calls/CallButtons";
import { ContactForm } from "./ContactForm";
import { ConsentPanel, ConversationsPanel, PrivacyPanel, SequencesPanel } from "./EngagementPanels";

export function ContactDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const perms = useMe().data?.permissions ?? [];
  const contact = useContact(id);
  const fields = useFields("contact");
  const labels = useFieldLabels("contact");
  const deals = useDeals({ contact_id: id }, can(perms, "deals:read"));
  const del = useDeleteContact();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  if (contact.isLoading) return <Spinner />;
  if (contact.error) {
    const notFound = contact.error instanceof ApiError && contact.error.status === 404;
    return <Alert>{notFound ? "This contact does not exist or you do not have access to it." : contact.error.message}</Alert>;
  }
  const c = contact.data!;

  const onDelete = async () => {
    if (!window.confirm(`Delete ${c.full_name}? This can be undone by an administrator.`)) return;
    await del.mutateAsync(c.id);
    navigate("/contacts", { replace: true });
  };

  const details: [string, string | null][] = [
    ["Email", c.email], ["Phone", c.phone], ["Job title", c.job_title], ["Company", c.company_name],
    ["Owner", c.owner?.full_name ?? null],
    ...(fields.data ?? []).map((f): [string, string | null] => [f.label, formatCustom(c.custom[f.key])]),
  ];

  return (
    <>
      <Link to="/contacts" className="mb-4 inline-block text-sm text-muted hover:text-brand">← All contacts</Link>
      <PageHeader title={c.full_name} subtitle={[c.job_title, c.account?.name ?? c.company_name].filter(Boolean).join(" · ") || undefined}
        actions={!editing && <>
          {can(perms, "activities:create") && <CallButtons contactId={c.id} name={c.full_name} phone={c.phone} />}
          {can(perms, "contacts:update") && <button type="button" className="btn-ghost" onClick={() => { setEditing(true); setSaved(false); }}>Edit</button>}
          {can(perms, "contacts:delete") && <button type="button" className="btn-danger" onClick={() => void onDelete()}>Delete</button>}
        </>} />
      {saved && <div className="mb-4"><Alert tone="ok">Changes saved.</Alert></div>}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <section className="card p-6" aria-label="Details">
            {editing ? (
              <ContactForm contact={c} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); setSaved(true); }} />
            ) : (
              <>
                <div className="mb-6 flex items-center gap-3"><Avatar name={c.full_name} /><span className="text-sm text-muted">Added {formatDate(c.created_at)}</span></div>
                <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                  <div><dt className="text-xs font-medium uppercase tracking-wide text-muted">Account</dt>
                    <dd className="mt-0.5 text-sm">{c.account ? <Link to={`/accounts/${c.account.id}`} className="text-brand hover:underline">{c.account.name}</Link> : <span className="text-muted">—</span>}</dd></div>
                  {details.map(([label, value]) => (
                    <div key={label}><dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt><dd className="mt-0.5 break-words text-sm">{value ?? <span className="text-muted">—</span>}</dd></div>
                  ))}
                </dl>
              </>
            )}
          </section>
          {can(perms, "deals:read") && !!deals.data?.items.length && (
            <section className="card" aria-label="Deals">
              <h2 className="border-b border-line px-5 py-3 font-semibold">Deals</h2>
              <ul className="divide-y divide-line">
                {deals.data.items.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <Link to={`/deals/${d.id}`} className="font-medium hover:text-brand">{d.name}<span className="block text-xs font-normal text-muted">{d.stage.name}</span></Link>
                    <span className="flex items-center gap-3 text-sm">{money(d.amount, d.currency)}<StatusBadge status={d.status} /></span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        <div className="min-w-0 space-y-6">
          <section className="card min-w-0 space-y-5 p-5" aria-label="Activity">
            <h2 className="font-semibold">Activity</h2>
            {can(perms, "activities:create") && <ActivityComposer target={{ type: "contact", id: c.id }} />}
            <Timeline target={{ type: "contact", id: c.id }} labels={labels} />
          </section>
          <ConsentPanel c={c} perms={perms} />
          {can(perms, "inbox:read") && <ConversationsPanel c={c} />}
          <SequencesPanel c={c} perms={perms} />
          <PrivacyPanel c={c} perms={perms} />
        </div>
      </div>
    </>
  );
}
